<template>
  <div class="md:ml4">
    <sonicu-header>
      Point Status Summary
      <span v-if="totalPoints" class="font-extralight text-sm ml-8 mb-0">{{ totalPoints }} points in use</span>
      <span v-if="displayPoints" class="font-extralight text-sm ml-8 mb-0">
            (+ {{ displayPoints }} SoniShield Display point<span v-if="displayPoints > 1">s</span>)
      </span>
    </sonicu-header>

    <div class="mt-3">
      <!-- Content goes here -->
      <div v-show="chartData.length > 0">
        <div id="desktop_summary_chart" class="h-64 hidden md:block">

        </div>
        <div id="mobile_summary_chart" class="h-48 block md:hidden">

        </div>
      </div>
      <div v-show="chartData.length === 0"
           class="flex flex-row justify-around">
        <div class="my-8 flex align-center">
                    <span class="lnr icon-loading animate-spin ml-3">
                    </span>
          <span class="mt-1 ml-2">Loading ... Please Wait.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {amchartsMixin} from "@/mixins/amchartsMixin";

import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import * as am5xy from "@amcharts/amcharts5/xy";

import axios from "axios";
export default {
  name: "DashboardPointStatusSummary",
  mixins: [SonicuCoreMixin, amchartsMixin],
  data() {
    return {
      data: {},
      loading: false,
      desktopChart: null,
      mobileChart: null,
      chartData: [],
      mobileData: [],
      colorKey: {
        green_threshold: am5.color('#38a169'),
        yellow_threshold: am5.color('#f6e05e'),
        red_threshold: am5.color('#c53030'),
        offline: am5.color('#a0aec0'),
      },
    }
  },
  mounted() {
    this.initializeAM5Charts();
    this.loadData();
  },
  methods: {
    loadData() {
      this.loading = true;
      axios.get(this.company.buildCompanyApiInternalRoute('dashboard/point-status-summary')).then(
          response => {
            this.data = response.data;
            this.loading = false
            this.buildData()
            this.makeDesktopPlot();
            this.makeMobileChart()
          });
    },
    makeDesktopPlot() {
      if (this.desktopChart) {
        this.desktopChart.dispose();
      }

      let root = am5.Root.new("desktop_summary_chart");

      let chart = root.container.children.push(
          am5percent.PieChart.new(root, {})
      );

      let series = chart.series.push(
          am5percent.PieSeries.new(root, {
            radius: am5.percent(75),
            valueField: "number",
            categoryField: "type",
            colors: am5.ColorSet.new(root, {
              colors: [
                am5.color("#38a169"),
                am5.color("#f6e05e"),
                am5.color("#c53030"),
                am5.color("#a0aec0")
              ]
            })
          })
      );

      series.slices.template.setAll({
        draggable: false,
        toggleKey: "none",
        cursorOverStyle: "pointer",
        tooltipText: "{value} {category}",
        propertyFields: {
          fill: "color"
        },
        strokeOpacity: 1
      });

      series.labels.template.setAll({
        text: "{value} {category}",
        maxWidth: 70,
        wrap: true,
        align: "right"
      })

      series.slices.template.events.on("click", (ev) => {
        const clickedSlice = ev.target;

        series.slices.each((slice) => {
          if (slice === clickedSlice) {
            let point = slice.dataItem.dataContext.currentDataSummaryFilter;
            window.location.href = this.company.buildCompanyRoute('reports/current-readings-report?status=' + point)
          }
        });
      });

      series.data.setAll(this.chartData);

      this.desktopChart = chart;
    },
    makeMobileChart() {
      if (this.mobileChart !== null) {
        this.mobileChart.dispose();
      }

      let root = am5.Root.new("mobile_summary_chart");

      let chart = root.container.children.push(
          am5xy.XYChart.new(root, {
            layout: root.verticalLayout
          })
      );

      let categoryAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        categoryField: 'row',
        visible: false,
      }));
      categoryAxis.get('renderer').grid.template.set('forceHidden', true); // This will hide the grid lines
      categoryAxis.data.setAll(this.mobileData);

      let valueAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, {}),
        min: 0,
        max: this.totalPoints,
        visible: false,
      }));
      valueAxis.get('renderer').grid.template.set('forceHidden', true); // This will hide the grid lines

      let legend = chart.children.unshift(
          am5.Legend.new(root, {
            x: am5.percent(50),
            centerX: am5.percent(50),
            layout: am5.GridLayout.new(root, {
              maxColumns: 2,
            }),
          }),
      );
      legend.labels.template.setAll({
        maxWidth: 1,
      });

      let createSeries = (field, name) => {
        let series = chart.series.push(am5xy.ColumnSeries.new(root, {
          name: name,
          xAxis: valueAxis,
          yAxis: categoryAxis,
          stacked: true,
          valueXField: field,
          categoryYField: 'row',
          fill: this.colorKey[field],
        }));

        series.columns.template.setAll({
          height: 40,
          fillOpacity: 0.8,
          strokeOpacity: 0,
          tooltipText: "{valueX} {name} points."
        })

        series.data.setAll(this.mobileData);
        legend.data.push(series);
      };

      createSeries('green_threshold', "In Range");
      createSeries('yellow_threshold', 'Caution');
      createSeries('red_threshold', 'Out of Range');
      createSeries('offline', 'Offline');

      legend.markers.template.setAll({
        width: 24,
        height: 24,
        fillOpacity: 1,
        fill: am5.color('#f6e05e'),
      })

      this.mobileChart = chart;
    },
    buildData() {
      this.chartData = [
        {
          type: 'In Range',
          number: this.data['green'],
          color: this.colorKey['green_threshold'],
          currentDataSummaryFilter: 1,
        },
        {
          type: 'Caution',
          number: this.data['yellow'],
          color: this.colorKey['yellow_threshold'],
          currentDataSummaryFilter: 2,
        },
        {
          type: 'Out of Range',
          number: this.data['red'],
          color: this.colorKey['red_threshold'],
          currentDataSummaryFilter: 3,
        },
        {
          type: 'Offline',
          number: this.data['offline'],
          color: this.colorKey['offline'],
          currentDataSummaryFilter: 0,
        },
      ]
      this.mobileData = [
        {
          row: '1',
          green_threshold: this.data['green'],
          yellow_threshold: this.data['yellow'],
          red_threshold: this.data['red'],
          offline: this.data['offline'],
          display_points: this.data['display'],
        },
      ]
    },
    beforeUnmount() {
      this.desktopChart.dispose()
      this.mobileChart.dispose()
    },
  },
  computed: {
    totalPoints(){
      if (this.mobileData.length === 0) {
        return null;
      }
      let row = this.mobileData[0]
      return row.green_threshold + row.yellow_threshold + row.red_threshold + row.offline
    },
    displayPoints(){
      if (this.mobileData.length === 0) {
        return null;
      }
      let row = this.mobileData[0]
      return row.display_points
    },
  }
}
</script>

<style scoped>
</style>